import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { RequireAuth, RedirectIfAuthenticated } from "./guards";
import { RouteError } from "./RouteError";

/**
 * Rotas dos tres apps (CLAUDE.md 8):
 *
 *   /         app dos pais      (guardian)   — mobile-first
 *   /painel   painel            (therapist)  — desktop
 *   /admin    admin             (operator)   — escopo separado
 *
 * Cada app e um chunk proprio (code-splitting por rota, RT-10): um
 * responsavel abrindo o app no celular no meio da rua nao deve baixar o
 * painel do terapeuta nem o admin junto.
 *
 * `/esqueci-senha`, `/ativar-conta` e `/redefinir-senha` ficam fora dos tres
 * shells: convite (RF-02.4) e reset (RF-02.5) valem para qualquer papel do
 * tenant (guardian ou therapist) e nao exigem sessao — o mesmo endpoint do
 * backend atende os dois fluxos (Api::V1::Auth::PasswordResetsController).
 */

const ParentsLayout = lazy(() => import("./layouts/ParentsLayout"));
const ParentsHomePage = lazy(() => import("./routes/parents/HomePage"));
const ParentsHistoryPage = lazy(() => import("./routes/parents/HistoryPage"));
const ParentsOrientationsPage = lazy(() => import("./routes/parents/OrientationsPage"));
const ParentsMessagesPage = lazy(() => import("./routes/parents/MessagesPage"));
const ParentsProfilePage = lazy(() => import("./routes/parents/ProfilePage"));
const ParentsLoginPage = lazy(() => import("./routes/parents/LoginPage"));

const ParentsVoiceCheckinPage = lazy(() => import("./routes/parents/VoiceCheckinPage"));
const ParentsSharePage = lazy(() => import("./routes/parents/SharePage"));
const SharedViewPage = lazy(() => import("./routes/SharedViewPage"));
const TherapistImportPage = lazy(() => import("./routes/therapist/ImportPage"));
const TherapistLayout = lazy(() => import("./layouts/TherapistLayout"));
const TherapistDashboardPage = lazy(() => import("./routes/therapist/DashboardPage"));
const TherapistLoginPage = lazy(() => import("./routes/therapist/LoginPage"));
const PatientsListPage = lazy(() => import("./routes/therapist/PatientsListPage"));
const PatientDetailPage = lazy(() => import("./routes/therapist/PatientDetailPage"));
const TherapistAlertsPage = lazy(() => import("./routes/therapist/AlertsPage"));
const TherapistCalendarPage = lazy(() => import("./routes/therapist/CalendarPage"));
const TherapistMessagesPage = lazy(() => import("./routes/therapist/MessagesPage"));
const TherapistTemplateLibraryPage = lazy(() => import("./routes/therapist/TemplateLibraryPage"));
const TherapistSettingsPage = lazy(() => import("./routes/therapist/SettingsPage"));

const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminTenantsPage = lazy(() => import("./routes/admin/TenantsPage"));
const AdminTenantDetailPage = lazy(() => import("./routes/admin/TenantDetailPage"));
const AdminPlatformConfigPage = lazy(() => import("./routes/admin/PlatformConfigPage"));
const AdminOperatorsPage = lazy(() => import("./routes/admin/OperatorsPage"));
const AdminLoginPage = lazy(() => import("./routes/admin/LoginPage"));

const ForgotPasswordPage = lazy(() => import("./routes/auth/ForgotPasswordPage"));
const FindTenantPage = lazy(() => import("./routes/auth/FindTenantPage"));
const ActivateAccountPage = lazy(() => import("./routes/auth/ActivateAccountPage"));
const ResetPasswordPage = lazy(() => import("./routes/auth/ResetPasswordPage"));

function RouteFallback() {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-live="polite">
      <span className="sr-only">Carregando</span>
      <div className="size-8 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function NotFoundPage() {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-sm font-medium text-primary-600">404</p>
      <h1 className="text-2xl font-semibold">Pagina nao encontrada</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        O endereco que voce abriu nao existe ou foi movido.
      </p>
    </main>
  );
}

const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
  // --- Visao publica de compartilhamento (RF-11.2): sem login ---------
  {
    path: "/compartilhado/:token",
    element: (
      <Lazy>
        <SharedViewPage />
      </Lazy>
    ),
  },

  // --- App dos pais -------------------------------------------------
  {
    path: "/entrar",
    element: (
      <Lazy>
        <RedirectIfAuthenticated>
          <ParentsLoginPage />
        </RedirectIfAuthenticated>
      </Lazy>
    ),
  },
  {
    path: "/",
    element: (
      <Lazy>
        <RequireAuth roles={["guardian"]} redirectTo="/entrar">
          <ParentsLayout />
        </RequireAuth>
      </Lazy>
    ),
    children: [
      {
        index: true,
        element: (
          <Lazy>
            <ParentsHomePage />
          </Lazy>
        ),
      },
      {
        path: "historico",
        element: (
          <Lazy>
            <ParentsHistoryPage />
          </Lazy>
        ),
      },
      {
        path: "orientacoes",
        element: (
          <Lazy>
            <ParentsOrientationsPage />
          </Lazy>
        ),
      },
      {
        path: "mensagens",
        element: (
          <Lazy>
            <ParentsMessagesPage />
          </Lazy>
        ),
      },
      {
        path: "contar-por-voz",
        element: (
          <Lazy>
            <ParentsVoiceCheckinPage />
          </Lazy>
        ),
      },
      {
        path: "compartilhar",
        element: (
          <Lazy>
            <ParentsSharePage />
          </Lazy>
        ),
      },
      {
        path: "perfil",
        element: (
          <Lazy>
            <ParentsProfilePage />
          </Lazy>
        ),
      },
    ],
  },

  // --- Painel do terapeuta ------------------------------------------
  {
    path: "/painel/entrar",
    element: (
      <Lazy>
        <RedirectIfAuthenticated>
          <TherapistLoginPage />
        </RedirectIfAuthenticated>
      </Lazy>
    ),
  },
  {
    path: "/painel",
    element: (
      <Lazy>
        <RequireAuth roles={["therapist"]} redirectTo="/painel/entrar">
          <TherapistLayout />
        </RequireAuth>
      </Lazy>
    ),
    children: [
      {
        index: true,
        element: (
          <Lazy>
            <TherapistDashboardPage />
          </Lazy>
        ),
      },
      {
        path: "pacientes",
        element: (
          <Lazy>
            <PatientsListPage />
          </Lazy>
        ),
      },
      {
        path: "pacientes/:id",
        element: (
          <Lazy>
            <PatientDetailPage />
          </Lazy>
        ),
      },
      {
        path: "importar",
        element: (
          <Lazy>
            <TherapistImportPage />
          </Lazy>
        ),
      },
      {
        path: "importar/:id",
        element: (
          <Lazy>
            <TherapistImportPage />
          </Lazy>
        ),
      },
      {
        path: "alertas",
        element: (
          <Lazy>
            <TherapistAlertsPage />
          </Lazy>
        ),
      },
      {
        path: "calendario",
        element: (
          <Lazy>
            <TherapistCalendarPage />
          </Lazy>
        ),
      },
      {
        path: "mensagens",
        element: (
          <Lazy>
            <TherapistMessagesPage />
          </Lazy>
        ),
      },
      {
        path: "biblioteca",
        element: (
          <Lazy>
            <TherapistTemplateLibraryPage />
          </Lazy>
        ),
      },
      {
        path: "configuracoes",
        element: (
          <Lazy>
            <TherapistSettingsPage />
          </Lazy>
        ),
      },
    ],
  },

  // --- Admin da plataforma ------------------------------------------
  {
    path: "/admin/entrar",
    element: (
      <Lazy>
        <RedirectIfAuthenticated scope="operator">
          <AdminLoginPage />
        </RedirectIfAuthenticated>
      </Lazy>
    ),
  },
  {
    path: "/admin",
    element: (
      <Lazy>
        <RequireAuth scope="operator" roles={["operator"]} redirectTo="/admin/entrar">
          <AdminLayout />
        </RequireAuth>
      </Lazy>
    ),
    children: [
      {
        index: true,
        element: (
          <Lazy>
            <AdminTenantsPage />
          </Lazy>
        ),
      },
      {
        path: "tenants/:id",
        element: (
          <Lazy>
            <AdminTenantDetailPage />
          </Lazy>
        ),
      },
      {
        path: "configuracoes",
        element: (
          <Lazy>
            <AdminPlatformConfigPage />
          </Lazy>
        ),
      },
      {
        path: "operators",
        element: (
          <Lazy>
            <AdminOperatorsPage />
          </Lazy>
        ),
      },
    ],
  },

  // --- Convite e reset de senha (RF-02.4, RF-02.5) — role-agnostico -
  { path: "/esqueci-senha", element: <Lazy><ForgotPasswordPage /></Lazy> },
  { path: "/encontrar-clinica", element: <Lazy><FindTenantPage /></Lazy> },
  { path: "/ativar-conta", element: <Lazy><ActivateAccountPage /></Lazy> },
  { path: "/redefinir-senha", element: <Lazy><ResetPasswordPage /></Lazy> },

  // Compatibilidade com o caminho antigo antes de a rota pt-BR existir.
  { path: "/login", element: <Navigate to="/entrar" replace /> },
  { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
